import HtmlInspectorSelect from './HtmlInspectorSelect';
import { useEffect, useMemo, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { FileType } from '../../common/constants';
import Uploader from '../Uploader';
import { fetchPriorityOptions, imageLoadingOptions } from './PageBuilderConstants';
import { usePageBuilder } from './PageBuilderContext';
import { getHtmlImageSelection, updateHtmlImage } from './htmlImages';
import styles from './BuilderInspector.module.css';
import BuilderInspectorHeader from './BuilderInspectorHeader';

export default function HtmlImageEditor({ block }) {
    const { selectedHtmlImage } = usePageBuilder();
    if (selectedHtmlImage?.blockId !== block.id) return null;
    return <HtmlImageFields key={`${block.id}:${selectedHtmlImage.index}`} block={block} />;
}

function HtmlImageFields({ block }) {
    const { selectedHtmlImage, updateBlock } = usePageBuilder();
    const html = block.data?.html || '';
    const selection = useMemo(() => selectedHtmlImage?.blockId === block.id
        ? getHtmlImageSelection(html, selectedHtmlImage.index) : null, [html, block.id, selectedHtmlImage]);
    const [draft, setDraft] = useState(null);
    useEffect(() => setDraft(selection), [selection]);
    if (!selection || !draft) return null;
    const changed = JSON.stringify(selection) !== JSON.stringify(draft);
    const setField = (name, value) => setDraft(previous => ({ ...previous, [name]: value }));
    const setSource = (index, name, value) => setDraft(previous => ({ ...previous,
        sources: previous.sources.map((source, i) => i === index ? { ...source, [name]: value } : source),
    }));
    const addSource = media => setDraft(previous => ({ ...previous,
        sources: [{ key: uuidv4(), originalIndex: null, media, srcset: '', type: '', sizes: '' }, ...previous.sources],
    }));
    const moveSource = (index, offset) => setDraft(previous => {
        const sources = [...previous.sources];
        [sources[index], sources[index + offset]] = [sources[index + offset], sources[index]];
        return { ...previous, sources };
    });
    const upload = (url, onComplete) => <div className={styles.upload}><Uploader preview={url} multiple={false} iconButtons type={FileType.Image}
        onComplete={files => { if (files?.[0]?.url) onComplete(files[0].url); }} /></div>;

    return <form className={styles.editor} aria-label="Modifica immagine HTML" onKeyDown={event => event.stopPropagation()}
        onSubmit={event => {
            event.preventDefault();
            if (!changed) return;
            updateBlock(block.id, { data: { ...block.data, html: updateHtmlImage(html, selectedHtmlImage.index, draft) } });
        }}>
        <BuilderInspectorHeader type="image" title="Immagine selezionata" description="Sorgenti, accessibilità e caricamento." badge="IMG" />
        <label>Immagine desktop / fallback
            <input value={draft.src} onChange={event => setField('src', event.target.value)} placeholder="https://…" />
        </label>
        {upload(draft.src, url => setField('src', url))}
        <label>Testo alternativo
            <input value={draft.alt} onChange={event => setField('alt', event.target.value)} />
        </label>
        <div className={styles.grid}>
        <label>Priorità
            <HtmlInspectorSelect value={draft.fetchpriority}
                options={[{ id: 'default', value: '', name: 'Predefinita' }, ...fetchPriorityOptions]}
                onChange={value => setField('fetchpriority', value)} />
        </label>
        <label>Caricamento
            <HtmlInspectorSelect value={draft.loading}
                options={[{ id: 'default', value: '', name: 'Predefinito' }, ...imageLoadingOptions]}
                onChange={value => setField('loading', value)} />
        </label>
        </div>
        <label>Decodifica
            <HtmlInspectorSelect value={draft.decoding}
                options={[{ id: '', value: '', name: 'Predefinita' }, { id: 'auto', value: 'auto', name: 'Automatica' }, { id: 'async', value: 'async', name: 'Asincrona' }, { id: 'sync', value: 'sync', name: 'Sincrona' }]}
                onChange={value => setField('decoding', value)} />
        </label>
        <details open={Boolean(draft.srcset || draft.sizes)}>
            <summary>Dimensioni e risoluzioni dell’immagine</summary>
            <div className={styles.fields}>
                {Object.entries({ width: 'Larghezza', height: 'Altezza', srcset: 'Srcset immagine', sizes: 'Sizes immagine' }).map(([name, label]) =>
                    <label key={name}>{label}<input value={draft[name]} onChange={event => setField(name, event.target.value)} /></label>)}
            </div>
        </details>
        <strong className={styles.sectionTitle}>Varianti responsive</strong>
        <p>Il browser usa la prima sorgente compatibile. L’immagine di fallback resta disponibile quando nessuna variante corrisponde.</p>
        <div className={styles.actions}>
            <button type="button" onClick={() => addSource('(max-width: 767px)')}>Aggiungi mobile</button>
            <button type="button" onClick={() => addSource('(min-width: 768px)')}>Aggiungi desktop</button>
        </div>
        {draft.sources.map((source, index) => <fieldset className={styles.source} key={source.originalIndex == null ? source.key : `source-${source.originalIndex}`}>
            <legend>Sorgente {index + 1}</legend>
            <label>URL / srcset {index + 1}
                <input required value={source.srcset} onChange={event => setSource(index, 'srcset', event.target.value)} placeholder="mobile.webp oppure mobile.webp 1x, mobile@2x.webp 2x" />
            </label>
            {upload('', url => setSource(index, 'srcset', url))}
            <label>Media query {index + 1}<input value={source.media} onChange={event => setSource(index, 'media', event.target.value)} /></label>
            <label>Formato MIME {index + 1}<input value={source.type} placeholder="image/webp" onChange={event => setSource(index, 'type', event.target.value)} /></label>
            <label>Sizes {index + 1}<input value={source.sizes} placeholder="100vw" onChange={event => setSource(index, 'sizes', event.target.value)} /></label>
            <div className={styles.actions}>
                <button type="button" disabled={index === 0} aria-label={`Sposta sorgente ${index + 1} su`} onClick={() => moveSource(index, -1)}>↑</button>
                <button type="button" disabled={index === draft.sources.length - 1} aria-label={`Sposta sorgente ${index + 1} giù`} onClick={() => moveSource(index, 1)}>↓</button>
                <button className={styles.danger} type="button" onClick={() => setField('sources', draft.sources.filter((_, i) => i !== index))}>Rimuovi sorgente {index + 1}</button>
            </div>
        </fieldset>)}
        <div className={styles.footer}>
            <button type="button" disabled={!changed} onClick={() => setDraft(selection)}>Annulla</button>
            <button className={styles.primary} type="submit" disabled={!changed}>Applica immagine</button>
        </div>
    </form>;
}
